import { useTranslation } from 'react-i18next';

import { formatMileage, formatObservedDate } from './lib';
import type { VehicleDetailData, VehicleOpenDetailData } from './types';
import styles from './VehicleDetails.module.css';

interface VehicleDetailsProps {
  /** Объявление целиком: полная и открытая страницы описывают его одинаково (BE-580). */
  vehicle: VehicleDetailData | VehicleOpenDetailData;
  locale: string;
}

/** Пара «параметр — значение». `null` показывается как «Нет данных». */
interface SpecRow {
  key: string;
  value: string | null;
}

/**
 * Характеристики автомобиля (MVP_PRD §18).
 *
 * Общий блок публичной страницы (FE-053) и персональной карточки подбора
 * (FE-083): два набора полей неизбежно разошлись бы, а расхождение здесь
 * означало бы, что один и тот же автомобиль описан по-разному.
 *
 * Поле с отсутствующим значением **не скрывается**: пропуск создавал бы ложное
 * впечатление полноты, поэтому пустое значение показывается как «Нет данных».
 * Сведения из объявления вынесены в отдельный блок и названы заявлениями
 * продавца — CarViQ.DE их не проверяет (MVP_PRD §15).
 *
 * Автохауса здесь нет: с Epic 58 у экрана автомобиля свой блок автохауса с
 * именем, городом и ссылкой на объявление (FE-587), и второй карточки с тем же
 * именем рядом с ним быть не должно.
 */
export function VehicleDetails({ vehicle, locale }: VehicleDetailsProps) {
  const { t } = useTranslation();
  const noData = t('catalog.noData');

  const number = (value: number) => new Intl.NumberFormat(locale).format(value);
  const decimal = (value: number) =>
    new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(value);
  const dictionary = (prefix: string, value: string | null) =>
    value === null ? null : t(`${prefix}.${value}`, { defaultValue: value });

  const specs = vehicle.specs;
  const registration = formatObservedDate(`${specs.first_registration_date}T12:00:00Z`, locale);

  const powertrain: SpecRow[] = [
    { key: 'fuelType', value: dictionary('catalog.fuel', specs.fuel_type) },
    {
      key: 'power',
      value: specs.power_kw === null ? null : t('vehicle.specs.powerValue', { kw: specs.power_kw }),
    },
    {
      key: 'engineCapacity',
      value:
        specs.engine_capacity_cc === null
          ? null
          : t('vehicle.specs.engineValue', { value: number(specs.engine_capacity_cc) }),
    },
    { key: 'transmission', value: dictionary('catalog.transmission', specs.transmission) },
    { key: 'drivetrain', value: dictionary('catalog.drivetrain', specs.drivetrain) },
  ];

  const body: SpecRow[] = [
    { key: 'bodyType', value: dictionary('catalog.bodyType', specs.body_type) },
    { key: 'firstRegistration', value: registration },
    {
      key: 'mileage',
      value: t('catalog.spec.mileageValue', { value: formatMileage(specs.mileage_km, locale) }),
    },
    { key: 'doors', value: specs.doors === null ? null : String(specs.doors) },
    { key: 'seats', value: specs.seats === null ? null : String(specs.seats) },
    { key: 'color', value: specs.exterior_color },
    {
      key: 'trunkVolume',
      value:
        specs.trunk_volume_l === null
          ? null
          : t('vehicle.specs.litreValue', { value: number(specs.trunk_volume_l) }),
    },
  ];

  const consumption: SpecRow[] = [
    {
      key: 'consumptionFuel',
      value:
        specs.consumption_l_100km === null
          ? null
          : t('vehicle.specs.consumptionFuelValue', { value: decimal(specs.consumption_l_100km) }),
    },
    {
      key: 'consumptionElectric',
      value:
        specs.consumption_kwh_100km === null
          ? null
          : t('vehicle.specs.consumptionElectricValue', {
              value: decimal(specs.consumption_kwh_100km),
            }),
    },
    {
      key: 'co2',
      value:
        specs.co2_g_km === null ? null : t('vehicle.specs.co2Value', { value: specs.co2_g_km }),
    },
    { key: 'emissionClass', value: specs.emission_class },
  ];

  /** Сведения из объявления. Не проверка: формулировки не утверждают факт. */
  const listingClaims: SpecRow[] = [
    {
      key: 'owners',
      value: vehicle.history.owners_count === null ? null : String(vehicle.history.owners_count),
    },
    {
      key: 'serviceHistory',
      value: t(`vehicle.serviceHistory.${vehicle.history.service_history_status}`),
    },
    {
      key: 'accidentHistory',
      value: t(`vehicle.accidentHistory.${vehicle.history.accident_history_status}`),
    },
    { key: 'vin', value: t(`vehicle.vinStatus.${vehicle.history.vin_status}`) },
    { key: 'vatStatus', value: t(`vehicle.vatStatus.${vehicle.vat_status}`) },
  ];

  const specGroup = (titleKey: string, rows: SpecRow[]) => (
    <section className={styles.specGroup}>
      <h3 className={styles.specTitle}>{t(titleKey)}</h3>
      <dl className={styles.specList}>
        {rows.map((row) => (
          <div key={row.key} className={styles.specRow}>
            <dt>{t(`vehicle.specs.${row.key}`)}</dt>
            <dd className={row.value === null ? styles.specMissing : undefined}>
              {row.value ?? noData}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );

  return (
    <div className={styles.details}>
      <div className={styles.detailsMain}>
        {specGroup('vehicle.specs.groupPowertrain', powertrain)}
        {specGroup('vehicle.specs.groupBody', body)}
        {specGroup('vehicle.specs.groupConsumption', consumption)}

        {vehicle.feature_codes.length > 0 ? (
          <section className={styles.specGroup}>
            <h3 className={styles.specTitle}>{t('vehicle.features')}</h3>
            <ul className={styles.features}>
              {vehicle.feature_codes.map((code) => (
                <li key={code}>{t(`vehicle.feature.${code}`, { defaultValue: code })}</li>
              ))}
            </ul>
          </section>
        ) : null}

        {vehicle.description ? (
          <section className={styles.specGroup}>
            <h3 className={styles.specTitle}>{t('vehicle.description')}</h3>
            <p className={styles.description}>{vehicle.description}</p>
          </section>
        ) : null}
      </div>

      <aside className={styles.detailsAside}>
        <section className={styles.card}>
          {/* Заголовок и значения внутри уже говорят, что это сведения из
              объявления («В объявлении не указана», «Продавец заявляет…»).
              Отдельная строка о том, чего CarViQ.DE не проверяет, была бы
              повтором подвального дисклеймера (MVP_PRD §5). */}
          <h3 className={styles.specTitle}>{t('vehicle.listingClaims')}</h3>
          <dl className={styles.specList}>
            {listingClaims.map((row) => (
              <div key={row.key} className={styles.specRow}>
                <dt>{t(`vehicle.specs.${row.key}`)}</dt>
                <dd className={row.value === null ? styles.specMissing : undefined}>
                  {row.value ?? noData}
                </dd>
              </div>
            ))}
          </dl>
        </section>
      </aside>
    </div>
  );
}
